import { defineNuxtPlugin } from '#app';
import crc32 from 'crc-32';

// Define interfaces for Tweakwise data structures
interface TweakwiseLayer {
    push: (event: TweakwiseEvent) => void;
}

interface TweakwiseEvent {
    event: string;
    data?: {
        profileKey?: string;
        productKeys?: string[];
        productKey?: string;
        searchTerm?: string;
        itemId?: string;
        requestId?: string;
    };
    'twa.start'?: number;
    'twa.instance'?: string;
}

// Extend the window interface to include tweakwise properties
declare global {
    interface Window {
        tweakwiseLayer: TweakwiseLayer;
        _twa: string;
    }
}

// Define the interface for the Tweakwise service
interface TweakwiseService {
    trackPurchase: (productIDs: string[]) => void;
    trackProductView: (productID: string) => void;
    trackSearch: (searchTerm: string) => void;
    getVisitorId: () => string;
    trackItemClick: (itemId: string, requestId: string) => void;
}

export default defineNuxtPlugin(nuxtApp => {
    // Only run on client-side
    if (process.server) return;

    const config = useRuntimeConfig();
    const domainId = config.public.tweakwise.domainId;

    const getVisitorId = (): string => {
        let visitorId = localStorage.getItem('tw_visitor_id');
        if (!visitorId) {
            visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now();
            localStorage.setItem('tw_visitor_id', visitorId);
        }
        return visitorId;
    };

    // Function to get current profile key

    // Initialize Tweakwise
    (function (w: Window, d: Document, l: string, i: string, p: string | null, u: string) {
        w['_twa'] = l;
        w[l] = w[l] || [];
        w[l].push({ 'twa.start': new Date().getTime(), event: 'twa.js' });
        w[l].push({ 'twa.instance': i, event: 'twa.init' });
        const f = d.getElementsByTagName('script')[0];
        const j = d.createElement('script');
        j.async = true;
        j.src = u;
        f.parentNode?.insertBefore(j, f);
    })(
        window,
        document,
        'tweakwiseLayer',
        config.public.tweakwise.instanceKey,
        getVisitorId(),
        '//navigator-analytics.tweakwise.com/bundles/scout.js',
    );

    // Create helper functions for tracking events
    const tweakwise: TweakwiseService = {
        getVisitorId(): string {
            return getVisitorId();
        },
        trackPurchase(productIDs: string[]): void {
            window.tweakwiseLayer.push({
                event: 'purchase',
                data: {
                    profileKey: getVisitorId(),
                    productKeys: productIDs,
                },
            });
        },

        trackProductView(productID: string): void {
            window.tweakwiseLayer.push({
                event: 'productView',
                data: {
                    profileKey: getVisitorId(),
                    productKey: productID,
                },
            });
        },

        trackSearch(searchTerm: string): void {
            window.tweakwiseLayer.push({
                event: 'search',
                data: {
                    profileKey: getVisitorId(),
                    searchTerm,
                },
            });
        },

        trackItemClick(itemId: string, requestId: string): void {
            window.tweakwiseLayer.push({
                event: 'itemClick',
                data: { itemId, requestId },
            });
        },
    };

    // Provide to components
    nuxtApp.provide('tweakwise', tweakwise);
});
